import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { silentLines, silentLinesMessage } from '@reelmachine/core';
import { useReel, useReelAction, useSelectTake } from '../../api/hooks';
import { formatDuration } from '../../lib/format';
import { Icon } from '../../ui/Icon';
import { ErrorView, HistoryButton, Loading, Screen } from '../../ui/Screen';
import { Stepper } from '../../ui/Stepper';
import { useToast } from '../../ui/Toast';
import { Teleprompter } from './Teleprompter';

export function RecordScreen() {
  const { reelId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const q = useReel(reelId);
  const select = useSelectTake(reelId);
  const render = useReelAction(reelId, 'render');
  const [prompter, setPrompter] = useState(false);

  if (q.isError) return <ErrorView back="/" error={q.error} />;
  if (q.isPending) return <Loading />;
  const { reel, script, takeUrls } = q.data;
  const selected = reel.selectedTake;
  const takeUrl = selected ? takeUrls?.[selected] : undefined;
  const silent = script ? silentLines(script) : [];

  const makeVideo = async () => {
    try {
      await render.mutateAsync();
      navigate(`/reels/${reelId}/video`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not start the video.');
    }
  };

  return (
    <>
      <Screen
        title={reel.title}
        back="/"
        right={<HistoryButton reelId={reelId} />}
        bar={
          <>
            <button type="button" className="btn sec" disabled={!script} onClick={() => setPrompter(true)}>
              <Icon name="mic" /> Teleprompter
            </button>
            <button
              type="button"
              className="btn"
              disabled={!selected || render.isPending || silent.length > 0}
              onClick={() => void makeVideo()}
            >
              Make video
            </button>
          </>
        }
        barHint={silent.length ? silentLinesMessage(silent) : selected ? undefined : 'Record a take first.'}
      >
        <Stepper reel={reel} current="record" />
        {takeUrl && (
          <video
            key={takeUrl}
            className="player take-review"
            src={takeUrl}
            controls
            playsInline
            preload="metadata"
            aria-label={`Take ${selected}`}
          />
        )}
        <fieldset className="card" style={{ border: 0, margin: 0 }}>
          <legend className="label" style={{ padding: 0, float: 'left', marginBottom: 4 }}>
            Takes
          </legend>
          {reel.takes.length === 0 ? (
            <p className="muted" style={{ clear: 'both' }}>
              No takes yet. Open the teleprompter and read the script.
            </p>
          ) : (
            <div style={{ clear: 'both' }}>
              {reel.takes.map((t) => (
                <label className="take" key={t.n} htmlFor={`take-${t.n}`}>
                  <span className="row" style={{ gap: 10 }}>
                    <input
                      type="radio"
                      id={`take-${t.n}`}
                      name="take"
                      value={t.n}
                      checked={selected === t.n}
                      onChange={() => select.mutate(t.n, { onError: (e) => toast(e.message) })}
                    />
                    Take {t.n}
                    {script && t.scriptVersion !== script.version && <span className="chip warn">Older script</span>}
                  </span>
                  <span className="nums">{formatDuration(t.durationMs)}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
      </Screen>
      {prompter && script && (
        <Teleprompter
          reelId={reelId}
          orgId={reel.orgId}
          lines={script.lines.map((l) => l.text)}
          onClose={() => setPrompter(false)}
        />
      )}
    </>
  );
}
